import { Button } from '../Button'
import { useIntl } from 'react-intl'
import { SearchInput } from '../SearchInput'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { expertSkillsSchema } from '../../validators/expertSkillsSchema'
import { CheckboxCombo } from './components/CheckboxCombo'
import { useExpertSkills } from './useExpertSkills'
import { Spinner } from '../Spinner'

interface Props {
  expertId: number
}

export const ExpertSkillsDesktop = ({ expertId }: Props) => {
  const { formatMessage } = useIntl()
  const {
    expert,
    isLoadingExpertSkills,
    isLoadingAreaSkills,
    isLoadingDomainSkills,
    filteredAreaSkills,
    filteredDomainSkills,
    areaSkills,
    domainSkills,
    translationLabel,
    isUpdatingSkills,
    updateSkills,
    handleSearchAreaSkills,
    handleSearchDomainSkills,
  } = useExpertSkills(expertId)

  const methods = useForm({
    resolver: yupResolver(expertSkillsSchema),
    defaultValues: {
      areaSkills: [] as any[],
      domainSkills: [] as any[],
    },
  })

  return (
    <div className="md:w-full lg:px-52">
      <header className="mb-6">
        <h2 className="text-base font-bold">
          {formatMessage({ id: 'SKILLS' })}
        </h2>
        <p className="text-base font-normal mb-14">
          {formatMessage({ id: 'ABLE_TO_TALK_ABOUT' })}
        </p>
      </header>
      <div className="flex gap-4 mb-12">
        <div className="flex flex-col p-6 border w-96 h-96 border-grey-500 rounded-2xl">
          <div className="flex flex-row items-center justify-between w-full mb-6">
            <h2 className="text-lg font-medium">
              {formatMessage({ id: 'AREA' })}
            </h2>
            <SearchInput
              className="w-1/2"
              placeholder={formatMessage({ id: 'SEARCH' })}
              onSearch={handleSearchAreaSkills}
              disabled={isLoadingAreaSkills || isLoadingExpertSkills}
            />
          </div>

          {!isLoadingAreaSkills && !isLoadingExpertSkills ? (
            <FormProvider {...methods}>
              <form
                id="area-form"
                onSubmit={methods.handleSubmit(updateSkills)}
                className="overflow-y-auto"
              >
                {areaSkills && (
                  <CheckboxCombo
                    name="areaSkills"
                    checkedOptions={expert?.areaSkills || []}
                    options={filteredAreaSkills || areaSkills}
                    translationLabel={translationLabel}
                  />
                )}
              </form>
            </FormProvider>
          ) : (
            <div className="flex items-center justify-center h-full">
              <Spinner />
            </div>
          )}
        </div>
        <div className="flex flex-col p-6 border w-96 h-96 border-grey-500 rounded-2xl">
          <div className="flex flex-row items-center justify-between w-full mb-6">
            <h2 className="text-lg font-medium">
              {formatMessage({ id: 'DOMAIN' })}
            </h2>
            <SearchInput
              className="w-1/2"
              placeholder={formatMessage({ id: 'SEARCH' })}
              onSearch={handleSearchDomainSkills}
              disabled={isLoadingDomainSkills || isLoadingExpertSkills}
            />
          </div>
          {!isLoadingExpertSkills && !isLoadingDomainSkills ? (
            <FormProvider {...methods}>
            <form
              id="area-form"
              onSubmit={methods.handleSubmit(updateSkills)}
              className="overflow-y-auto"
            >
              {domainSkills && (
                <CheckboxCombo
                  name="domainSkills"
                  checkedOptions={expert?.domainSkills || []}
                  options={filteredDomainSkills || domainSkills}
                  translationLabel={translationLabel}
                />
              )}
            </form>
          </FormProvider>
          ) : (
            <div className="flex items-center justify-center h-full">
              <Spinner />
            </div>
          )}
        </div>
      </div>
      <Button
        form="area-form"
        type="submit"
        isLoading={isUpdatingSkills}
        disabled={!methods.formState.isDirty}
        className="w-32"
      >
        {formatMessage({ id: 'SAVE' })}
      </Button>
    </div>
  )
}
